/** Compact one-line summaries for collapsed rule rows.
 *
 * Pure string builders — the row shell renders them ellipsized. Negation is
 * shown by the row's NOT chip, never repeated here.
 */
import type { RulesetBrief, RuleWire } from '@/api/notificationRulesets';
import { RULE_TYPE_EXPR } from '@/api/notificationRulesets';

import {
  CHECKIN_SOURCE_OPTIONS,
  DEPRECATED_TPLS,
  DT_RANGE_COMPARISONS,
  EXPR_OPS,
  OTA_TRISTATE_OPTIONS,
  PARTICIPANT_SOURCE_OPTIONS,
  PRECHECKIN_STATUS_OPTIONS,
  RULE_TEMPLATE_LABELS,
  SETTLEMENT_METHOD_OPTIONS,
  SURVEY_STATE_OPTIONS,
  TIME_UNIT_OPTIONS,
  TPL,
} from './ruleCatalog';
import { chipSummaryLabels } from '@/components/TagConditionInput';

import { draftsFromDetail, effectiveDtTz } from './ruleSerialize';
import { ruleTemplateLabel } from './ruleCatalog';
import type { RuleDraft } from './ruleTypes';
import { tourTagsFromArgs } from './tourTagArgs';

const label = (options: { value: string; label: string }[], value: unknown): string =>
  options.find((o) => o.value === String(value))?.label ?? String(value);

const labels = (options: { value: string; label: string }[], values: unknown): string =>
  Array.isArray(values) ? values.map((v) => label(options, v)).join(', ') : '';

function nameList(items: unknown, pick: (item: Record<string, unknown>) => string): string {
  if (!Array.isArray(items) || items.length === 0) return '';
  const names = items.map((item) =>
    item && typeof item === 'object' ? pick(item as Record<string, unknown>) : String(item),
  );
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
}

function deltaSummary(args: Record<string, unknown>): string {
  const delta = Number(args.delta ?? 0);
  const unit =
    TIME_UNIT_OPTIONS.find((u) => u.value === Number(args.unit)) ??
    TIME_UNIT_OPTIONS.find((u) => u.value === 3600)!;
  const amount = unit.value ? delta / unit.value : delta;
  return `within ${Number.isInteger(amount) ? amount : amount.toFixed(1)} ${unit.label}`;
}

function trimmed(s: string, max = 40): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** One-line summary of the rule's configuration ('' when the label says it all). */
export function summarizeRule(rule: RuleDraft): string {
  if (rule.type === RULE_TYPE_EXPR) {
    const op = EXPR_OPS.find((o) => o.value === rule.op);
    const parts = [trimmed(rule.field || '(no field)'), op?.label ?? `op ${rule.op}`];
    if (op?.needsValue !== false) parts.push(trimmed(rule.value ?? ''));
    return parts.join(' · ');
  }
  const tpl = rule.tpl;
  const args = (rule.args ?? {}) as Record<string, unknown>;
  if (tpl !== null && DEPRECATED_TPLS.has(tpl)) return 'deprecated — read-only';
  if (tpl !== null && !(tpl in RULE_TEMPLATE_LABELS)) return 'unknown template — read-only';

  switch (tpl) {
    case TPL.USER_LOGGED_IN: {
      const period = args.period;
      return period ? `within the last ${period}s` : 'logged in at least once';
    }
    case TPL.USER_COUNTRY:
    case TPL.PHONE_COUNTRY: {
      const codes = args.country;
      return Array.isArray(codes) && codes.length
        ? codes.map((c) => String(c).toUpperCase()).join(', ')
        : 'no countries selected';
    }
    case TPL.USER_LANG: {
      const codes = args.lang;
      return Array.isArray(codes) && codes.length ? codes.join(', ') : 'any language';
    }
    case TPL.USER_OTA: {
      const parts: string[] = [];
      const otaNames = nameList(args.ota, (o) => String(o.name ?? o.id));
      parts.push(otaNames || 'any OTA');
      for (const key of ['unique_emails', 'direct', 'app_promotion'] as const) {
        const v = Number(args[key] ?? 0);
        if (v) parts.push(label(OTA_TRISTATE_OPTIONS[key].map((o) => ({ value: String(o.value), label: o.label })), v));
      }
      return parts.join(' · ');
    }
    case TPL.IS_FIELD_AFFECTED: {
      const fields = args.affected_fields;
      return Array.isArray(fields) && fields.length
        ? nameList(fields, (f) => String(f))
        : 'default field set';
    }
    case TPL.IS_UPDATED_VIA:
      return labels(PARTICIPANT_SOURCE_OPTIONS, args.update_sources) || 'no methods selected';
    case TPL.SURVEY_STATE:
      return labels(SURVEY_STATE_OPTIONS, args.survey_state) || 'no states selected';
    case TPL.USER_HAS_TAG_CATEGORY:
    case TPL.TOUR_HAS_TAG_CATEGORY:
    case TPL.OTA_HAS_TAG_CATEGORY:
    case TPL.TOUR_OPERATOR_HAS_TAG_CATEGORY:
    case TPL.ORDER_HAS_TAG_CATEGORY:
    case TPL.ACCOUNT_HAS_TAG_CATEGORY:
      return nameList(args.tag_categories, (c) => String(c)) || 'no categories selected';
    case TPL.USER_HAS_TAG:
    case TPL.TOUR_HAS_TAG:
    case TPL.OTA_HAS_TAG:
    case TPL.TOUR_OPERATOR_HAS_TAG:
    case TPL.ORDER_HAS_TAG:
    case TPL.ACCOUNT_HAS_TAG:
      return (
        nameList(args.tags, (t) => `${String(t.category ?? '')}: ${String(t.name ?? t.id)}`) ||
        'no tags selected'
      );
    case TPL.IS_REBOOKING: {
      const r = (args.rebooking ?? {}) as Record<string, unknown>;
      const parts = [r.from ? 'new booking' : null, r.to ? 'old booking' : null].filter(Boolean);
      return parts.length ? parts.join(' + ') : 'nothing selected';
    }
    case TPL.IS_FIELD_PRESENT: {
      const fields = Array.isArray(args.fields) ? args.fields : [];
      const mode = args.match_all === false ? 'any' : 'all';
      return `${fields.length} field check${fields.length === 1 ? '' : 's'} (match ${mode})`;
    }
    case TPL.TRIP_BEGINS_WITHIN_X_TIME:
      return deltaSummary(args);
    case TPL.BOOKING_DATETIME_RANGE:
      return `booked ${deltaSummary(args)} of start`;
    case TPL.REVIEW_IS_POSITIVE_NEGATIVE: {
      const parts = [
        args.no_review ? 'no review' : null,
        args.positive_review ? 'positive' : null,
        args.negative_review ? 'negative' : null,
      ].filter(Boolean);
      return parts.length ? parts.join(' / ') : 'nothing selected';
    }
    case TPL.USER_IS_NEAR_LOCATION:
      return `${args.latitude ?? '?'}, ${args.longitude ?? '?'} r=${args.radius ?? '?'}m (${
        args.fetch_method === 'ip_address' ? 'IP' : 'GPS'
      })`;
    case TPL.REVIEW_RATING:
      return `${args.low ?? 0} – ${args.high ?? 5}`;
    case TPL.HAS_OTHER_TOURS: {
      const time = (args.time ?? {}) as Record<string, unknown>;
      const refs = (
        args.time_ref && typeof args.time_ref === 'object' ? args.time_ref : {}
      ) as Record<string, unknown>;
      // Non-default reference moments (CP-8264) surface as e.g. "past@end".
      const flag = (key: 'future' | 'active' | 'past') => {
        if (!time[key]) return null;
        const ref = refs[key];
        return typeof ref === 'string' && ref !== 'now' ? `${key}@${ref}` : key;
      };
      const parts = [
        args.same_departure ? 'same departure' : null,
        args.same_tour ? 'same tour' : null,
        flag('future'),
        flag('active'),
        flag('past'),
      ].filter(Boolean);
      const tours = nameList(args.tours, (t) => String(t.name ?? t.id));
      if (tours) parts.push(tours);
      const tags = tourTagsFromArgs(args.tour_tags);
      if (tags.chips.length) {
        parts.push(`tagged ${chipSummaryLabels(tags).join(tags.matchMode === 'and' ? ' & ' : ' | ')}`);
      }
      return parts.length ? parts.join(' · ') : 'nothing selected';
    }
    case TPL.RULESET: {
      const nested = rule.nested_ruleset;
      if (!nested) return 'no ruleset selected';
      return nested.name ? `${nested.name} (#${nested.id})` : `ruleset #${nested.id}`;
    }
    case TPL.EVENT_TYPE:
      return nameList(args.events, (e) => String(e.label ?? e.value)) || 'no events selected';
    case TPL.DELIVERY_METHOD: {
      const methods = nameList(args.delivery_methods, (m) => String(m.name ?? m.id));
      const suffix = args.allow_undefined ? ' (ignore unknown)' : '';
      return (methods || 'no methods selected') + suffix;
    }
    case TPL.CHECKIN_SOURCE:
      return labels(CHECKIN_SOURCE_OPTIONS, args.sources) || 'no sources selected';
    case TPL.TRIP_TIME_RANGE: {
      const parts: string[] = [];
      if (args.future) parts.push(`future +${Math.round(Number(args.future_delta ?? 0) / 60)}m`);
      if (args.present)
        parts.push(
          `present ${Math.round(Number(args.low_delta ?? 0) / 60)}m…${Math.round(Number(args.high_delta ?? 0) / 60)}m`,
        );
      if (args.past) parts.push(`past −${Math.round(Number(args.past_delta ?? 0) / 60)}m`);
      return parts.length ? parts.join(' · ') : 'nothing selected';
    }
    case TPL.PRECHECKIN_STATUS:
      return labels(PRECHECKIN_STATUS_OPTIONS, args.precheckin_statuses) || 'no statuses selected';
    case TPL.EMAIL_RECEIPT:
      return args.email_receipt ? 'receipt permitted' : 'receipt not permitted';
    case TPL.SETTLEMENT_METHOD:
      return labels(SETTLEMENT_METHOD_OPTIONS, args.settlement_method) || 'no methods selected';
    case TPL.HAS_SUB_BOOKINGS:
      return args.scope === 'current' ? 'current participant only' : 'any participant in the booking';
    case TPL.DATETIME_WITHIN_RANGE:
    case TPL.DATE_WITHIN_RANGE:
    case TPL.TIME_WITHIN_RANGE: {
      const comparison =
        DT_RANGE_COMPARISONS.find((c) => c.value === args.comparison)?.label ?? '(no comparison)';
      const bounds = [args.from, args.to].filter(Boolean).join(' … ');
      const clock = effectiveDtTz(tpl, args.tz) === 'utc' ? 'UTC' : 'tour local';
      return `${trimmed(String(args.input ?? ''), 30) || '(no input path)'} ${comparison}${
        bounds ? ` ${bounds}` : ''
      } (${clock})`;
    }
    default:
      return ''; // no-arg templates: the label is the summary
  }
}

// ---------------------------------------------------------------------------
// Included-ruleset preview (the tpl-48 editor's right-hand panel)
// ---------------------------------------------------------------------------

export interface RulesetPreviewRow {
  key: number;
  negate: boolean;
  label: string;
  summary: string;
  /** Set on nested "Include ruleset" rows — the preview links instead of
   *  expanding (one level only, by design). */
  nestedRulesetId?: number;
}

/** Compact one-line-per-rule preview rows for an included ruleset's rules. */
export function buildRulesetPreviewRows(
  rules: RuleWire[],
  refs?: { rulesets?: Record<string, RulesetBrief> },
): RulesetPreviewRow[] {
  return draftsFromDetail(rules, refs).map((d) => {
    if (d.type !== RULE_TYPE_EXPR && d.tpl === TPL.RULESET) {
      const nested = d.nested_ruleset;
      const row: RulesetPreviewRow = {
        key: d.id,
        negate: d.negate,
        label: 'Include ruleset',
        summary: nested?.name ? `${nested.name} (#${nested.id})` : `ruleset #${nested?.id ?? '?'}`,
      };
      if (nested) row.nestedRulesetId = nested.id;
      return row;
    }
    return {
      key: d.id,
      negate: d.negate,
      label: d.type === RULE_TYPE_EXPR ? 'Expression' : ruleTemplateLabel(d.tpl),
      summary: summarizeRule(d),
    };
  });
}
